import { View, Text, Avatar, Icon, AvatarProps } from '@/components'
import { AppIcon, StyledComponent, useNestedStylesByKey } from '@codeleap/styles'
import { PropsOf } from '@codeleap/types'
import React from 'react'
import { AvatarInfoParts } from '@/config/stylesheets'
import { StyleRegistry, StyleSheets } from '@/config'
import { useStylesFor } from '@codeleap/web/src/lib'

type AvatarInfoProps = Omit<PropsOf<typeof View>, 'style'> & {
  title: string
  description?: string
  avatar?: AvatarProps['image']
  titleIcon?: AppIcon
  descriptionIcons?: AppIcon[]
  highlighted?: boolean
}

export const AvatarInfo: StyledComponent<typeof StyleSheets.AvatarInfoStyles, AvatarInfoProps> = (props) => {
  const {
    style,
    title,
    description,
    avatar,
    titleIcon,
    descriptionIcons,
    highlighted = false,
    ...viewProps
  } = props

  const styles = useStylesFor(AvatarInfo.styleRegistryName, style)
  const avatarStyles = useNestedStylesByKey('avatar', styles)

  const getStylesByKey = React.useCallback((styleKey: AvatarInfoParts) => ([
    styles?.[styleKey],
    highlighted && styles[`${styleKey}:highlighted`],
  ]), [styles, highlighted])

  const descriptionStyles = React.useMemo(() => getStylesByKey('description'), [getStylesByKey])

  return (
    <View style={styles.wrapper} {...viewProps}>
      <Avatar
        debugName={`avatarInfo-header`}
        name={title}
        image={avatar}
        style={avatarStyles}
        disabled
        firstNameOnly={false}
      />

      <View style={styles.contentWrapper}>
        <View style={styles.titleWrapper}>
          <Text text={title} style={styles.title} />
          {titleIcon ? <Icon name={titleIcon} style={styles.titleIcon} /> : null}
        </View>

        {!!description && (
          <View style={descriptionStyles}>
            <View style={styles.descriptionRow}>
              {descriptionIcons?.map((iconName, index) => (
                <Icon
                  key={`avatar-description-icon-${index}`}
                  name={iconName}
                  style={styles.descriptionIcon}
                />
              ))}
              <Text
                text={description}
                style={styles.description}
              />
            </View>
          </View>
        )}
      </View>
    </View>
  )
}

AvatarInfo.styleRegistryName = 'AvatarInfo'
AvatarInfo.elements = [
  'wrapper',
  'contentWrapper',
  'titleWrapper',
  'title',
  'titleIcon',
  'descriptionWrapper',
  'description',
  'descriptionIcon',
  'descriptionRow',
  'avatar',
]

StyleRegistry.registerComponent(AvatarInfo)
